import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Mail, Phone } from "lucide-react";
import { IMAGES, API, fetchInquiries } from "@/lib/api";

const KEY = "hue_inbox_pass";

export default function Inbox() {
  const [pass, setPass] = useState(() => sessionStorage.getItem(KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const load = useCallback(async (code) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchInquiries(code);
      setItems(data);
      setAuthed(true);
      sessionStorage.setItem(KEY, code);
      setPass(code);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Incorrect passcode.");
        setAuthed(false);
        sessionStorage.removeItem(KEY);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pass) load(pass);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    await load(input.trim());
  };

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setPass("");
    setAuthed(false);
    setItems([]);
    setInput("");
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center px-6">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
          data-testid="inbox-gate"
        >
          <img src={IMAGES.logo} alt="Hue Creative" className="h-9 w-auto mb-10" />
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#9c7a4e] mb-4">
            <Lock size={13} /> Studio Inbox
          </div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-[#9c7a4e] mb-1 block" htmlFor="pc">Passcode</label>
          <input
            id="pc"
            data-testid="inbox-passcode"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter passcode"
            className="w-full bg-transparent border-b border-[#d6ccbb] focus:border-[#23221f] outline-none py-3 text-[#23221f] font-light"
          />
          {error && <p className="mt-3 text-sm text-[#a3452f]" data-testid="inbox-error">{error}</p>}
          <button
            type="submit"
            data-testid="inbox-submit"
            disabled={loading}
            className="mt-8 w-full text-[12px] tracking-[0.2em] uppercase px-8 py-4 bg-[#23221f] text-[#F5F3EE] hover:bg-[#9c7a4e] transition-colors duration-500 disabled:opacity-60"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F3EE]">
      <header className="sticky top-0 z-10 bg-[#F5F3EE]/90 backdrop-blur border-b border-[#e3ddd0]">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logo} alt="Hue" className="h-7 w-auto" />
            <div>
              <p className="font-display text-xl text-[#23221f] leading-none">Studio Inbox</p>
              <p className="text-[11px] tracking-[0.16em] uppercase text-[#9c7a4e] mt-1">{items.length} inquiries</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="inbox-refresh" onClick={() => load(pass)} className="p-2 text-[#6b6459] hover:text-[#23221f]" aria-label="Refresh">
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </button>
            <button data-testid="inbox-logout" onClick={logout} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6b6459] hover:text-[#23221f]">
              <LogOut size={15} /> Lock
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="inbox-list">
        {items.length === 0 ? (
          <p className="text-[#6b6459] font-light py-20 text-center">No inquiries yet.</p>
        ) : (
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.id} className="bg-[#FBFAF6] border border-[#e8e2d6] p-6" data-testid="inbox-item">
                <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                  <h3 className="font-display text-2xl text-[#23221f]">{it.name}</h3>
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#9c7a4e]">
                    {(it.source || "").replace(/-/g, " ")} · {fmt(it.created_at)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#4a453d] mb-4">
                  <a href={`mailto:${it.email}`} className="flex items-center gap-2 link-underline"><Mail size={14} />{it.email}</a>
                  {it.phone && <span className="flex items-center gap-2"><Phone size={14} />{it.phone}</span>}
                  {it.project_type && <span className="text-[#6b6459]">{it.project_type}</span>}
                </div>
                <p className="text-[#23221f] font-light leading-relaxed whitespace-pre-wrap">{it.message}</p>
                {!it.email_sent && (
                  <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-[#a3452f]">Email not sent (Resend not configured)</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
